import { createAsyncThunk } from '@reduxjs/toolkit';
import { captureException } from '@sentry/browser';
import instance from '../../axiosInstance';
import Agent from '../../interfaces/agent';
import { deserializeData } from '../../utils/deserializer';
import type { State } from '..';
import { fetchAgentCommissionModels } from './commissionModels';
import { fetchAgentTags } from './tags';
import {
  AgentLiveTransferRequest,
  AgentLiveTransferRequestDisposition,
} from './types';

/**
 * Tags and commission models are auxiliary: a failure of their endpoints must
 * not fail the agent load or save. Report it and fall back to the values on
 * the agent payload.
 */
const orFallback = <T,>(request: Promise<T>): Promise<T | undefined> =>
  request.catch((error) => {
    captureException(error);
    return undefined;
  });

const fetchAgentTagsOrFallback = (agentId: string | number) =>
  orFallback(fetchAgentTags(agentId));

const fetchAgentCommissionModelsOrFallback = (agentId: string | number) =>
  orFallback(fetchAgentCommissionModels(agentId));

/// /
// Async Thunk Actions
/// /
export const fetchAgentById = createAsyncThunk<Agent, string | number>(
  'agents/fetchAgentById',
  async (agentId) => {
    const [response, tags, commissionModels] = await Promise.all([
      instance.get(`/deals/agent/${agentId}/`),
      fetchAgentTagsOrFallback(agentId),
      fetchAgentCommissionModelsOrFallback(agentId),
    ]);
    return {
      ...response.data,
      tags: tags ?? response.data.tags,
      commission_models_accepted:
        commissionModels ?? response.data.commission_models_accepted,
    };
  },
);

export const patchAgent = createAsyncThunk<Agent, any, { state: State }>(
  'agents/patchAgent',
  async (agent, { getState }) => {
    const agentId = agent.id;
    const { data } = agent;
    const response = await instance.patch(`/deals/agent/${agentId}/`, {
      ...data,
    });
    const storedAgent = getState().agents.agentsMap[agentId];

    // Writes still go through /deals/agent. Only when this patch changed tags
    // or commission models do we re-read them from their endpoints; otherwise
    // keep what is already in the store so /deals/agent's copy doesn't
    // replace it.
    const [tags, commissionModels] = await Promise.all([
      data && 'tags' in data
        ? fetchAgentTagsOrFallback(agentId)
        : storedAgent?.tags,
      data && 'commission_models_accepted' in data
        ? fetchAgentCommissionModelsOrFallback(agentId)
        : storedAgent?.commission_models_accepted,
    ]);

    return {
      ...response.data,
      tags: tags ?? response.data.tags,
      commission_models_accepted:
        commissionModels ?? response.data.commission_models_accepted,
    };
  },
);

export const fetchAgentLiveTransferRequestsByDealId = createAsyncThunk<
  AgentLiveTransferRequest[],
  { dealId: string }
>('agents/fetchAgentLiveTransferRequestsByDealId', async ({ dealId }) => {
  const response = await instance.get(
    `v4/agent-live-transfer/requests/?filter[deal]=${dealId}&page[size]=100`,
  );

  const data = deserializeData<AgentLiveTransferRequest[]>(response.data);

  if (!data || !data.length) {
    return [];
  }

  return data;
});

export const fetchAgentLiveTransferRequestDispositions = createAsyncThunk<
  AgentLiveTransferRequestDisposition[]
>('agents/fetchAgentLiveTransferRequestDispositions', async () => {
  const response = await instance.get(
    `/v4/agent-live-transfer/request-dispositions/`,
  );

  const data = deserializeData<AgentLiveTransferRequestDisposition[]>(
    response.data,
  );

  if (!data || !data.length) {
    return [];
  }

  return data;
});
